/**
 * T-29 · Los esquemas de las entradas, en un solo sitio.
 *
 * **Se validan en los dos lados con la misma definición.** El navegador para no
 * mandar basura y decirlo antes de esperar; el servidor porque el navegador no
 * es de fiar — nadie impide mandar la petición con `curl`.
 *
 * Que vivan aquí y no duplicados es lo que evita el caso peor: que el front
 * acepte algo que el servidor rechaza, o al revés. Dos validaciones que no
 * coinciden son peores que una sola.
 *
 * ── Por qué hacía falta ─────────────────────────────────────────────────────
 * Hasta hoy la API confiaba en los tipos de TypeScript, que **no existen en
 * tiempo de ejecución**. `POST /autenticacion/codigo` declaraba
 * `{ telefono?: string }` y recibía lo que fuera. Que no explotara era suerte:
 * `normalizarTelefono` rechaza lo que no sea un teléfono.
 *
 * Los estándares lo dicen entero: «entrada del usuario tratada como hostil en
 * toda ruta, incluidas las que sólo usa el cajero».
 */
import { z } from 'zod';
import { PALETAS } from './paleta.js';
/**
 * Un teléfono llega como lo escribió una persona: con espacios, guiones, o en
 * cualquiera de las cuatro formas heredadas mexicanas. **Aquí sólo se comprueba
 * que sea texto con un tamaño razonable**; normalizarlo es de `normalizarTelefono`,
 * que sabe de planes de numeración, y validarlo dos veces con dos criterios
 * distintos es cómo se acaba rechazando un número que sí vale.
 */
const telefono = z.string().trim().min(8).max(24);
export const esquemaPedirCodigo = z.object({
    telefono,
}).strict();
export const esquemaAbrirSesion = z.object({
    // Los tokens de Supabase son JWT: tres partes separadas por punto. No se
    // valida la firma aquí —eso lo hace Supabase— sino que tenga forma de token,
    // para no gastar un viaje de red en una cadena cualquiera.
    token: z.string().trim().min(20).max(4096),
    // Opcional: sólo viene cuando la persona es personal de varias cafeterías y
    // ya eligió una.
    tenantId: z.uuid().optional(),
}).strict();
/**
 * T-12 · Abrir sesión de consola: el token del SMS **y** el segundo factor.
 *
 * El código admite las dos formas porque el servidor no puede decir cuál va a
 * mandar el operador: seis dígitos del TOTP, o uno de recuperación. Se acota por
 * tamaño y alfabeto, no por cuál es —distinguirlos aquí obligaría a que el
 * mensaje de error dijera cuál se intentó, y eso le diría a quien prueba en qué
 * camino está.
 */
/**
 * `ADR-024` · La puerta de la consola: usuario, contraseña y TOTP.
 *
 * Antes era `{ token, codigo }` — el token del SMS que Supabase había
 * verificado. Se fue el SMS: ataba la entrada a tener cobertura y a un proveedor
 * configurado, costaba por mensaje, y era el más débil de los tres factores.
 *
 * El TOTP **no** se fue, y no va a irse: esta puerta crea inquilinos, crea
 * dueños e imprime papeles de QR. Con un solo factor sería el único sitio del
 * sistema donde una contraseña filtrada basta.
 */
export const esquemaAbrirSesionDePlataforma = z.object({
    //  Se normaliza a minúsculas aquí y no sólo en el servidor: `Isaac` e `isaac`
    //  son el mismo operador, y el `check` de la 045 sólo acepta minúsculas. Sin
    //  esto, teclear la mayúscula da «no se pudo entrar» sin ninguna pista.
    usuario: z.string().trim().toLowerCase().min(3).max(32).regex(/^[a-z][a-z0-9._-]*$/, 'El usuario empieza por letra y lleva sólo letras, números, punto, guion y guion bajo.'),
    //  Sin tope alto bajo: una contraseña larga es mejor, y `argon2` no se inmuta.
    //  El mínimo de doce es el de la OWASP para lo que además lleva segundo factor.
    //  **No se recorta con `trim`**: un espacio al final es parte de la contraseña
    //  de quien la eligió así, y recortarlo la cambiaría en silencio.
    contrasena: z.string().min(12).max(256),
    codigo: z.string().trim().min(6).max(20).regex(/^[A-Za-z0-9-]+$/, 'El código lleva sólo letras, números y guiones.'),
}).strict();
/**
 * T-11 · Dar de alta una cafetería desde la consola.
 *
 * El mismo esquema lo usa el formulario de la consola y el endpoint, que es el
 * punto de tenerlo aquí: si el navegador y el servidor validaran cosas distintas,
 * el operador vería «guardado» y la cafetería no existiría.
 *
 * Los límites no son decorativos. El identificador viaja en la URL que escanea
 * un cliente y el nombre sale en su pantalla; los dos se quedan cortos a
 * propósito, porque lo que no cabe en una mesa no sirve.
 */
export const esquemaAltaDeCafeteria = z.object({
    // Minúsculas, números y guiones entre medias. Es lo que aparece en la URL, y
    // por eso no admite mayúsculas ni acentos: una URL que no se puede dictar por
    // teléfono es una URL que alguien va a teclear mal.
    slug: z.string().trim().min(3).max(40).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Sólo minúsculas, números y guiones entre medias.'),
    nombre: z.string().trim().min(2).max(80),
    // Derivada de `PALETAS` y no copiada: la lista de paletas tiene ya dos copias
    // —el objeto del sistema de diseño y el CHECK de `tenants.brand_palette`— y una
    // tercera escrita a mano fue lo que dejó a `casa` fuera de la base durante un
    // día. Aquí no puede desincronizarse: si nace una paleta, este esquema la
    // acepta el mismo día, y si se retira, deja de aceptarla.
    //
    // Y deja de ser `string`: hasta hoy una clave inventada pasaba la validación y
    // moría contra el CHECK, o sea un 500 con el texto de un constraint de
    // Postgres en vez de un 400 diciendo cuál es la lista.
    paleta: z.enum(Object.keys(PALETAS)),
    sucursal: z.string().trim().min(2).max(60),
    // Sale de una lista en el formulario, no se escribe: una zona horaria mal
    // tecleada desplaza los cortes del día y nadie lo relaciona con esto.
    zona: z.string().trim().min(3).max(60),
    telefonoDuena: z.string().trim().min(8).max(24),
    nombreDuena: z.string().trim().min(2).max(80),
    // Al menos uno: una cafetería sin módulos entra a su panel y no ve nada.
    modulos: z.array(z.string().trim().min(1).max(40)).min(1).max(20),
}).strict();
/**
 * T-28 · Cambiar un módulo de una cafetería que ya existe.
 *
 * `ADR-011` separa las dos columnas y este esquema respeta la separación:
 * `estado` es intención humana y `vence` es un hecho de cobro. **La fecha no
 * apaga nada**; alimenta el aviso del panel. Se pueden mandar por separado.
 *
 * > [!info] `vence: null` y «no mandes `vence`» son cosas distintas
 * > Ausente significa «no toques la fecha». Nulo explícito significa «quítala»,
 * > que es un valor con significado: *sin vencimiento*. Mezclarlos haría
 * > imposible retirar una fecha ya puesta.
 */
/**
 * Los tres estados de `ADR-011`, en un solo sitio. La API los usa para tipar el
 * dominio y la consola para validar lo que le llega: si algún día se añade un
 * cuarto, las dos se enteran en el mismo commit o ninguna compila.
 */
export const esquemaEstadoDeModulo = z.enum(['active', 'suspended', 'closed']);
export const esquemaModuloEnLista = z.object({
    clave: z.string(),
    nombre: z.string(),
    estado: esquemaEstadoDeModulo,
    /** ISO 8601, o nulo — «sin vencimiento», que es un valor y no una ausencia. */
    vence: z.string().nullable(),
});
export const esquemaCafeteriaEnLista = z.object({
    tenantId: z.string(),
    nombre: z.string(),
    /** Nulo mientras no tenga slug vigente: recién dada de alta, todavía no lo hay. */
    slug: z.string().nullable(),
    modulos: z.array(esquemaModuloEnLista),
    /**
     * `T-69` · `M-14` — cuándo se tocó por última vez su menú. **Nulo es «nunca»**,
     * y es el caso más fuerte del tablero, no un hueco.
     *
     * Sólo viene con el módulo `menu` **activo**: una cafetería que no lo contrató
     * no tiene menú que abandonar, y una suspendida o cerrada ya se fue. Por eso
     * es opcional además de nulo: ausente y nulo dicen cosas distintas —«no aplica»
     * y «nunca la tocó»— y aplanarlas convertiría a media lista en abandonada.
     */
    ultimoToqueDelMenu: z.string().nullable().optional(),
    /**
     * `T-74` · El logotipo que subió el dueño, o nulo.
     *
     * La URL **pública** de Storage — la misma que sirve el menú del comensal, así
     * que no revela nada que no vea cualquiera que abra ese menú. Enrique la
     * necesita para bajárselo y quitarle el fondo antes de ponerlo en el papel del
     * QR: el original viene con fondo, y sobre un display oscuro un fondo blanco
     * es un rectángulo.
     */
    logoUrl: z.string().nullable(),
});
export const esquemaListaDeCafeterias = z.object({
    cafeterias: z.array(esquemaCafeteriaEnLista),
});
export const esquemaResultadoDelCambio = z.object({
    clave: z.string(),
    estado: esquemaEstadoDeModulo,
    vence: z.string().nullable(),
    creado: z.boolean(),
    sinCambios: z.boolean(),
});
export const esquemaCambioDeModulo = z.object({
    estado: esquemaEstadoDeModulo.optional(),
    vence: z.iso.datetime({ offset: true }).nullable().optional(),
    /**
     * Lo que la pantalla creía ver, y `null` significa «creía que esta cafetería
     * no tenía este módulo». Si la realidad es otra, el cambio se rechaza en vez
     * de pisar lo que hizo alguien más.
     *
     * Obligatorio a propósito: opcional, el día con prisa se omite y el control
     * de concurrencia desaparece sin que nadie lo decida.
     */
    estadoEsperado: esquemaEstadoDeModulo.nullable(),
}).strict().refine((c) => c.estado !== undefined || c.vence !== undefined, {
    message: 'Hay que cambiar algo: el estado, la fecha de vencimiento, o las dos.',
});
//# sourceMappingURL=esquemas.js.map